import { useCan, usePlanIncludes } from "@luckymenu/staff-core";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

/**
 * A function key for the whole screen while `enabled` (ux-design.md,
 * Keyboard shortcuts): F2 quick bill, F8 bill, F9 payment. Ignored with a
 * modifier, or when something else handled the key first.
 */
export function useShortcut(key: string, handler: () => void, enabled = true): void {
	const latest = useRef(handler);
	latest.current = handler;
	useEffect(() => {
		if (!enabled) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key !== key || event.defaultPrevented) return;
			if (event.ctrlKey || event.metaKey || event.altKey) return;
			event.preventDefault();
			latest.current();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [key, enabled]);
}

/** Ctrl+P (Cmd+P on a Mac) prints on this screen instead of the browser's own print, while `enabled`. */
export function usePrintShortcut(handler: () => void, enabled = true): void {
	const latest = useRef(handler);
	latest.current = handler;
	useEffect(() => {
		if (!enabled) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key.toLowerCase() !== "p" || !(event.ctrlKey || event.metaKey)) return;
			if (event.altKey || event.shiftKey || event.defaultPrevented) return;
			event.preventDefault();
			latest.current();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [enabled]);
}

/** F2 opens the quick bill from any screen, for roles that take orders and bill them. */
export function useQuickBillKey(): void {
	const navigate = useNavigate();
	// Both hooks always run: `&&` would skip the second until the membership loads.
	const mayBill = useCan("bill.create");
	const hasBilling = usePlanIncludes("billing");
	const bills = mayBill && hasBilling;
	const orders = useCan("order.create");
	useShortcut("F2", () => void navigate({ to: "/bill" }), bills && orders);
}
